/**
 * GenerationModeToggle
 *
 * Floating button to toggle between Standard and Studio generation UI
 * Persists preference in localStorage
 *
 * @accessibility WCAG 2.1 AA compliant
 */

import { useNavigate, useLocation } from 'react-router-dom';
import { FlaskConical, Sparkles } from 'lucide-react';
const STORAGE_KEY = 'goki_generation_mode';
type GenerationMode = 'standard' | 'studio';

export function GenerationModeToggle() {
  const navigate = useNavigate();
  const location = useLocation();
  const mode: GenerationMode = location.pathname.includes('/studio') ? 'studio' : 'standard';

  const handleToggle = () => {
    const newMode: GenerationMode = mode === 'standard' ? 'studio' : 'standard';

    // Save preference
    localStorage.setItem(STORAGE_KEY, newMode);

    // Navigate to new mode
    const targetPath = newMode === 'studio'
      ? '/v2/tools/studio/single-shot'
      : '/v2/tools/generate/single-v2';

    navigate(targetPath);
  };

  const isStudio = mode === 'studio';

  return (
    <button
      onClick={handleToggle}
      className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-full shadow-lg transition-all duration-200 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2"
      style={{
        backgroundColor: isStudio ? 'rgb(147 51 234)' : 'rgb(249 115 22)',
        color: 'white',
      }}
      aria-label={isStudio ? 'Switch to Standard Mode' : 'Switch to Studio Mode'}
      title={isStudio ? 'Switch to Standard Mode' : 'Switch to Studio Mode'}
    >
      {isStudio ? (
        <>
          <Sparkles className="h-5 w-5" />
          <span className="font-medium text-sm">Standard</span>
        </>
      ) : (
        <>
          <FlaskConical className="h-5 w-5" />
          <span className="font-medium text-sm">Studio</span>
        </>
      )}
    </button>
  );
}
